import { derivedBudgetRow } from "../features/budget/budgetGridMath.js";
import { EXPENSE_TYPE_ONGOING, normalizeExpenseType } from "../lib/expenseType.js";
import {
  filterExpenseRowsThroughCurrentMonth,
  filterExpenseRowsThroughToday
} from "../lib/finance/projectionMath.js";

function buildCategoryExpenseKey(row) {
  if (row?.categoryId) {
    return `id:${row.categoryId}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

function groupExpenseRowsByCategory(expenseRows = []) {
  return expenseRows.reduce((groups, row) => {
    if (normalizeExpenseType(row.type) !== EXPENSE_TYPE_ONGOING) {
      return groups;
    }

    const key = buildCategoryExpenseKey(row);
    const current = groups.get(key) ?? [];
    current.push(row);
    groups.set(key, current);
    return groups;
  }, new Map());
}

function sumExpenseAmounts(expenseRows = []) {
  return expenseRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
}

function buildBudgetCategoryKey(row) {
  if (row?.id) {
    return `id:${row.id}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

function hydrateBudgetRows(rows, expenseRows, dateContext) {
  const actualExpenseRows = filterExpenseRowsThroughToday(expenseRows, dateContext);
  const impliedExpenseRows = filterExpenseRowsThroughCurrentMonth(expenseRows, dateContext);
  const actualOngoingExpenseRowsByCategory = groupExpenseRowsByCategory(actualExpenseRows);
  const ongoingExpenseRowsByCategory = groupExpenseRowsByCategory(impliedExpenseRows);
  const irregularExpenseRowsByCategory = actualExpenseRows.reduce((groups, row) => {
    if (normalizeExpenseType(row.type) !== "irregular") {
      return groups;
    }

    const key = buildCategoryExpenseKey(row);
    const current = groups.get(key) ?? [];
    current.push(row);
    groups.set(key, current);
    return groups;
  }, new Map());

  return rows.map((row) => ({
    ...row,
    ongoingSpent: sumExpenseAmounts(actualOngoingExpenseRowsByCategory.get(buildBudgetCategoryKey(row)) ?? []),
    irregularSpent: sumExpenseAmounts(irregularExpenseRowsByCategory.get(buildBudgetCategoryKey(row)) ?? []),
    ...derivedBudgetRow(row, ongoingExpenseRowsByCategory.get(buildBudgetCategoryKey(row)) ?? [], dateContext)
  }));
}

export function createBudgetPageData(rows = [], expenseRows = [], dateContext) {
  const hydratedRows = hydrateBudgetRows(rows, expenseRows, dateContext);

  return {
    key: "budget",
    rows: hydratedRows,
    expenseRows
  };
}
